import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TableSort from 'components/Table/TableSort';
import { SORT_BY_OPTIONS_EXCLUDE } from 'constants/general';

import type ISelectSupplierTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  onDeleteItem,
  onSortChange,
  orderBy,
  locationsCountries,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Supplier table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {headCells.map(({
              id, label,
            }, index) => (
              <TableCell head className={styles.tableCell} key={id}>
                <div>
                  <span className={clsx(styles.tableCellLabel, {
                    [styles.tableCellLabelFirst]: index === 2,
                    [styles.tableCellLabelSecond]: index === 1,
                  })}
                  >
                    {id !== SORT_BY_OPTIONS_EXCLUDE.ACTION
                    && id !== SORT_BY_OPTIONS_EXCLUDE.SOURCE ? (
                      <TableSort
                        active={orderBy.includes(id) || orderBy.includes(`-${id}`)}
                        onClickSort={() => onSortChange(id)}
                      >
                        <span>{t(label)}</span>
                      </TableSort>
                      ) : (<span>{t(label)}</span>)}
                  </span>
                </div>
              </TableCell>
            ))}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <span>{locationCountry(item.countryCode || item.country)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {item.categoryName || hyphen}
                </TableCell>
                <TableCell>
                  <div className={styles.sourceContainer}>
                    {item.sources.map((src, srcIndex) => (
                      <span key={`${item.id}-${src.name}-${src.link}`}>
                        <a href={src.link} target="_blank" rel="noopener noreferrer">
                          {src.name}
                        </a>
                        {srcIndex < item.sources.length - 1 && ', '}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {item.regions.length > 0 ? item.regions.join(', ') : hyphen}
                </TableCell>
                <TableCell>
                  {item.sensitivityName || hyphen}
                </TableCell>
                <TableCell>
                  <IconButton
                    icon={<TrashIcon />}
                    onClick={() => onDeleteItem?.(item.id || -1)}
                  />
                </TableCell>
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTable;
